/**
 * Schnellsuche (Cmd/Ctrl-K) — Command-Palette über alle sichtbaren Meldungen.
 *
 * Debounced gegen /reports/suche — durchsucht alle in der Übersicht sichtbaren
 * Infos (Titel, Beschreibungen, Akzeptanzkriterien, Melder, Ninox-Stelle,
 * #Nummer), Mehrwort mit UND-Semantik. Bei mehreren Anwendungen der Instanz
 * filterbar per App-Chip. Treffer öffnen das Meldungs-Detail (Entwürfe: die
 * Erfassung).
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { CornerDownLeft, Search } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { api } from "@/services/api"
import { STATUS_FARBE, STATUS_LABEL, TYP_LABEL } from "@/lib/labels"
import type { Report } from "@/types"

/** Öffnet die Such-Palette programmatisch (Suchen-Knopf in der Topbar). */
export function suchPaletteOeffnen() {
  window.dispatchEvent(new CustomEvent("such-palette-oeffnen"))
}

export function SuchPalette() {
  const navigate = useNavigate()
  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState("")
  const [begriff, setBegriff] = useState("") // debounced
  const [appFilter, setAppFilter] = useState<number | null>(null)
  const [aktiv, setAktiv] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Cmd/Ctrl-K global — öffnet bzw. schließt die Palette; zusätzlich per
  // Custom-Event vom Suchen-Knopf in der Topbar (suchPaletteOeffnen)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOffen((o) => !o)
      }
    }
    const onOeffnen = () => setOffen(true)
    window.addEventListener("keydown", onKey)
    window.addEventListener("such-palette-oeffnen", onOeffnen)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("such-palette-oeffnen", onOeffnen)
    }
  }, [])

  // Beim Öffnen: Eingabe fokussieren und zurücksetzen
  useEffect(() => {
    if (offen) {
      setEingabe("")
      setBegriff("")
      setAktiv(0)
      // nach dem Rendern fokussieren
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [offen])

  // Debounce (150 ms) — schnell genug zum Mittippen, schont das Backend
  useEffect(() => {
    const t = setTimeout(() => setBegriff(eingabe.trim()), 150)
    return () => clearTimeout(t)
  }, [eingabe])

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
    enabled: offen,
  })
  const appName = new Map((anwendungen ?? []).map((a) => [a.id, a.name]))
  const mehrereApps = (anwendungen?.length ?? 0) > 1

  const { data: treffer, isFetching } = useQuery({
    queryKey: ["reports", "suche", begriff, appFilter],
    queryFn: ({ signal }) => api.reports.suche(begriff, appFilter ?? undefined, signal),
    enabled: offen && begriff.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })
  const ergebnisse: Report[] = begriff.length > 0 ? (treffer ?? []) : []

  useEffect(() => {
    setAktiv(0)
  }, [begriff, appFilter])

  const oeffnen = useCallback(
    (report: Report) => {
      setOffen(false)
      navigate(
        report.status === "entwurf" ? `/melden?entwurf=${report.id}` : `/meldungen/${report.id}`
      )
    },
    [navigate]
  )

  if (!offen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/55 p-4 pt-[12vh]"
      onMouseDown={() => setOffen(false)}
    >
      <div
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-[14px] bg-card shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOffen(false)
              if (e.key === "ArrowDown") {
                e.preventDefault()
                setAktiv((i) => Math.min(i + 1, ergebnisse.length - 1))
              }
              if (e.key === "ArrowUp") {
                e.preventDefault()
                setAktiv((i) => Math.max(i - 1, 0))
              }
              if (e.key === "Enter" && ergebnisse[aktiv]) oeffnen(ergebnisse[aktiv])
            }}
            placeholder="Meldungen durchsuchen — Titel, Beschreibung, Melder, Ninox-Stelle, #Nummer …"
            className="w-full bg-transparent py-3.5 text-[15px] outline-none placeholder:text-muted-foreground/70"
          />
          <kbd className="shrink-0 rounded border bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
            Esc
          </kbd>
        </div>

        {mehrereApps && (
          <div className="flex flex-wrap gap-1.5 border-b bg-muted/40 px-4 py-2.5">
            <button
              type="button"
              onClick={() => setAppFilter(null)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                appFilter === null
                  ? "bg-primary text-primary-foreground"
                  : "border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              Alle Anwendungen
            </button>
            {(anwendungen ?? []).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setAppFilter((f) => (f === a.id ? null : a.id))}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  appFilter === a.id
                    ? "bg-primary text-primary-foreground"
                    : "border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {a.name}
              </button>
            ))}
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto">
          {begriff.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Tippe, um zu suchen — z.&nbsp;B. „Export“, „Filter“ oder „#128“.
            </p>
          )}
          {begriff.length > 0 && !isFetching && ergebnisse.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Keine Treffer für „{begriff}“.
            </p>
          )}
          {ergebnisse.map((report, i) => (
            <button
              key={report.id}
              type="button"
              onClick={() => oeffnen(report)}
              onMouseEnter={() => setAktiv(i)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
                i === aktiv ? "bg-primary/5" : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">
                  {report.titel || <em className="text-muted-foreground">ohne Titel</em>}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  #{report.id}
                  {report.typ ? ` · ${TYP_LABEL[report.typ]}` : ""}
                  {mehrereApps && appName.has(report.applicationId)
                    ? ` · ${appName.get(report.applicationId)}`
                    : ""}
                  {report.forgejoIssueNr ? ` · Issue #${report.forgejoIssueNr}` : ""}
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
              >
                {STATUS_LABEL[report.status]}
              </span>
              {i === aktiv && (
                <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
